/**
 * LandingProfielen — Humanaize × AIVD Matcher landing.
 *
 * Glass pill nav, hero with eyebrow + headline + subline, two path cards
 * (Bekijk profielen / Start intake), then a professional grid of engineer
 * tiles. Light theme, brand gradient on white.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ENGINEERS } from './data/engineerShowcase';
import DetacheringDossier from './components/DetacheringDossier';
import './landingProfielen.css';

function HumanaizeLogo({ size = 28 }) {
  return (
    <div className="brand">
      <svg width={size} height={size} viewBox="0 0 64 64" fill="none" aria-hidden="true">
        <defs>
          <linearGradient id="hgl1" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#3b82f6" />
            <stop offset="100%" stopColor="#a855f7" />
          </linearGradient>
          <linearGradient id="hgl2" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#a855f7" />
            <stop offset="100%" stopColor="#ec4899" />
          </linearGradient>
          <linearGradient id="hgl3" x1="0" y1="1" x2="1" y2="0">
            <stop offset="0%" stopColor="#ec4899" />
            <stop offset="100%" stopColor="#f59e0b" />
          </linearGradient>
        </defs>
        <polygon points="32,4 60,20 60,44 32,60 4,44 4,20" fill="#fff" stroke="url(#hgl1)" strokeWidth="1.5" />
        <polygon points="32,4 60,20 32,36 4,20" fill="url(#hgl1)" opacity="0.92" />
        <polygon points="4,20 32,36 32,60 4,44" fill="url(#hgl2)" opacity="0.92" />
        <polygon points="60,20 32,36 32,60 60,44" fill="url(#hgl3)" opacity="0.92" />
      </svg>
      <span className="brand-word">HUMANAIZE</span>
    </div>
  );
}

function ProfilesIcon() {
  return (
    <svg width="40" height="40" viewBox="0 0 48 48" fill="none">
      <defs>
        <linearGradient id="pic1" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#3b82f6" />
          <stop offset="100%" stopColor="#a855f7" />
        </linearGradient>
      </defs>
      <rect x="4" y="8" width="18" height="24" rx="3" fill="none" stroke="url(#pic1)" strokeWidth="1.5" />
      <rect x="14" y="14" width="18" height="24" rx="3" fill="none" stroke="url(#pic1)" strokeWidth="1.5" opacity="0.7" />
      <rect x="24" y="20" width="18" height="24" rx="3" fill="url(#pic1)" opacity="0.18" stroke="url(#pic1)" strokeWidth="1.5" />
      <circle cx="33" cy="28" r="3" fill="url(#pic1)" />
      <path d="M27 38c0-3.3 2.7-6 6-6s6 2.7 6 6" stroke="url(#pic1)" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function IntakeIcon() {
  return (
    <svg width="40" height="40" viewBox="0 0 48 48" fill="none">
      <defs>
        <linearGradient id="iic1" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#ec4899" />
          <stop offset="100%" stopColor="#f59e0b" />
        </linearGradient>
      </defs>
      <rect x="8" y="6" width="28" height="36" rx="3" fill="none" stroke="url(#iic1)" strokeWidth="1.5" />
      <path d="M14 16h16M14 22h16M14 28h10" stroke="url(#iic1)" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="34" cy="34" r="8" fill="url(#iic1)" opacity="0.18" />
      <path d="M30 34l3 3 6-6" stroke="url(#iic1)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ProfileTile({ e, index, onClick }) {
  const initials = (e.name || '?')
    .split(' ')
    .map(p => p[0])
    .slice(0, 2)
    .join('');
  const cardNumber = String(index + 1).padStart(3, '0');
  const [photoFailed, setPhotoFailed] = useState(false);

  return (
    <button type="button" className="tile" aria-label={`Open dossier van ${e.name}`} onClick={onClick}>
      <div className="tile-id-row">
        <span className="tile-id">
          <span className="tile-id-dot" />NL-{cardNumber}-K
        </span>
        <span className="tile-status">AVAILABLE</span>
      </div>

      <div className="tile-photo">
        {e.photo && !photoFailed ? (
          <img src={e.photo} alt={e.name} onError={() => setPhotoFailed(true)} />
        ) : (
          <div className="tile-photo-fallback">{initials}</div>
        )}
        <span className="tile-photo-foil" aria-hidden />
      </div>

      <div className="tile-meta">
        <h3 className="tile-name">{e.name}</h3>
        <p className="tile-role">{e.role}</p>
      </div>

      <div className="tile-tags">
        {(e.skills || []).slice(0, 3).map((s, i) => (
          <span key={i} className="tile-tag">
            <span className="slash">//</span>{s}
          </span>
        ))}
      </div>

      <span className="tile-open-hint">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        Bekijk dossier
      </span>
    </button>
  );
}

export default function LandingProfielen() {
  const [activeEngineer, setActiveEngineer] = useState(null);

  useEffect(() => {
    const prev = document.title;
    document.title = 'Landing page - profielen';
    return () => { document.title = prev; };
  }, []);

  return (
    <div className="lp-prof">
      <div className="orb" aria-hidden />
      <div className="grid-bg" aria-hidden />

      <nav className="topnav">
        <a className="topnav-brand" href="https://humanaize.nl">
          <HumanaizeLogo size={28} />
        </a>
        <div className="topnav-mid">
          <a href="https://humanaize.nl">Home</a>
          <a href="https://humanaize.nl/over-ons">Over ons</a>
          <a href="https://humanaize.nl/contact">Contact</a>
        </div>
        <div className="topnav-right">
          <button className="topnav-lang" type="button">
            <span className="lang-dot" />NL
          </button>
        </div>
      </nav>

      <main className="main">
        <div className="split">
          <header className="split-head">
            <div className="eyebrow"><span className="eyebrow-dot" />DETACHERING &amp; MATCHING</div>
            <h1 className="headline">
              <span className="hl-a">Vind de juiste</span><br />
              <span className="hl-b">professional.</span>
            </h1>
            <p className="subline">
              Bekijk beschikbare profielen of laat ons de match maken via een intake.
              AI-ondersteund, menselijk afgerond.
            </p>
          </header>

          <div className="paths">
            <Link className="path path--intake" to="/app">
              <div className="path-top">
                <div className="path-badge">ONZE AI-MATCHER</div>
                <div className="path-arrow"><ArrowIcon /></div>
              </div>
              <div className="path-icon"><IntakeIcon /></div>
              <div className="path-body">
                <div className="path-label">Start een intake</div>
                <div className="path-desc">
                  Vertel ons je opdracht. Onze AI stelt vragen, denkt mee en levert
                  een shortlist die past bij jouw context.
                </div>
              </div>
              <div className="path-foot">
                <span className="path-host">Voice · dicteren · typen</span>
                <span className="path-cta">Starten →</span>
              </div>
            </Link>

            <a className="path path--profiles" href="#profielen">
              <div className="path-top">
                <div className="path-badge">VOOR OPDRACHTGEVERS</div>
                <div className="path-arrow"><ArrowIcon /></div>
              </div>
              <div className="path-icon"><ProfilesIcon /></div>
              <div className="path-body">
                <div className="path-label">Bekijk profielen</div>
                <div className="path-desc">
                  Direct toegang tot ons portfolio van beschikbare detacheringsprofielen.
                  Filter op rol, ervaring en beschikbaarheid.
                </div>
              </div>
              <div className="path-foot">
                <span className="path-host">{ENGINEERS.length} profielen beschikbaar</span>
                <span className="path-cta">Bekijken →</span>
              </div>
            </a>
          </div>
        </div>
      </main>

      <section className="profielen-section" id="profielen">
        <header className="profielen-head">
          <h2>Het <em>team.</em></h2>
          <span className="profielen-count">{ENGINEERS.length} profielen</span>
        </header>
        <div className="profielen-grid">
          {ENGINEERS.map((e, i) => (
            <ProfileTile
              key={e.id}
              e={e}
              index={i}
              onClick={() => setActiveEngineer({ ...e, _index: i })}
            />
          ))}
        </div>
      </section>

      {activeEngineer && (
        <DetacheringDossier
          engineer={activeEngineer}
          onClose={() => setActiveEngineer(null)}
        />
      )}

      <footer className="foot">
        <div className="foot-brand">© {new Date().getFullYear()} Humanaize</div>
        <div className="foot-meta">
          <a href="https://humanaize.nl/privacy">Privacy</a>
          <a href="https://humanaize.nl/voorwaarden">Voorwaarden</a>
          <a href="https://humanaize.nl/contact">Contact</a>
        </div>
      </footer>
    </div>
  );
}
